import React from "react";
import _ from "lodash";
import WebAsset from "react-icons/lib/md/web-asset";

import {
  Authorization,
  Privileges,
  hasPrivileges
} from "./modules/auth/authorization";
import Authentication from "./modules/auth/authentication";

import EndToEndContainer from "./modules/end-to-end/EndToEndContainer";
import ShipmentStatusViewContainer from "./modules/shipment-status/ShipmentStatusViewContainer";
import ShipmentDetailViewContainer from "./modules/shipment-detail/ShipmentDetailViewContainer";
import ShipmentSearchViewContainer from "./modules/shipment-search/ShipmentSearchViewContainer";
import UnderConstructionView from "./modules/appnav/UnderConstructionView";
import LocationsViewContainer from "./modules/locations/LocationsViewContainer";
import LocationDetailViewContainer from "./modules/location-detail/LocationDetailViewContainer";
import LocationEditViewContainer from "./modules/location-edit/LocationEditViewContainer";
import OrganizationsViewContainer from "./modules/organizations/OrganizationsViewContainer";
import UsersViewContainer from "./modules/users/UsersViewContainer";
import ProfileContainer from "./modules/profile/ProfileContainer";

import { StatusCodes } from "./modules/errors/ErrorView";
import ErrorContainer from "./modules/errors/ErrorContainer";
import CallbackContainer from "./modules/callback/CallbackContainer";

const authentication = new Authentication();
const ShipmentStatusViewAuthorization = Authorization(
  ShipmentStatusViewContainer,
  [Privileges.VIEW_SHIPMENTS, Privileges.MANAGE_SHIPMENTS]
);
const ShipmentDetailViewAuthorization = Authorization(
  ShipmentDetailViewContainer,
  [Privileges.VIEW_SHIPMENTS, Privileges.MANAGE_SHIPMENTS]
);
const ShipmentSearchViewAuthorization = Authorization(
  ShipmentSearchViewContainer,
  [Privileges.VIEW_SHIPMENTS, Privileges.MANAGE_SHIPMENTS]
);
const LocationsViewAuthorization = Authorization(LocationsViewContainer, [
  Privileges.MANAGE_LOCATIONS
]);
const LocationDetailAuthorization = Authorization(LocationDetailViewContainer, [
  Privileges.VIEW_LOCATIONS,
  Privileges.MANAGE_LOCATIONS
]);
const LocationEditViewAuthorization = Authorization(LocationEditViewContainer, [
  Privileges.MANAGE_LOCATIONS
]);
const OrganizationsViewAuthorization = Authorization(
  OrganizationsViewContainer,
  [Privileges.MANAGE_ORGANIZATIONS]
);
const UsersViewAuthorization = Authorization(UsersViewContainer, [
  Privileges.MANAGE_USERS
]);

export const routesMap = {
  ROOT: {
    path: "/",
    screen: {
      title: "Exceptions",
      id: "ROOT",
      icon: <WebAsset />,
      showInSidebar: hasPrivileges([
        Privileges.VIEW_SHIPMENTS,
        Privileges.MANAGE_SHIPMENTS
      ]),
      mainComponent: <ShipmentStatusViewAuthorization />
    }
  },
  END_TO_END: {
    path: "/locations",
    screen: {
      title: "Locations",
      id: "END_TO_END",
      icon: <WebAsset />,
      showInSidebar: true,
      mainComponent: <EndToEndContainer />
    }
  },
  SHIPMENT_DETAIL: {
    path: "/shipments/:shipment_id",
    screen: {
      title: "Shipment Detail",
      id: "SHIPMENT_DETAIL",
      showInSidebar: false,
      mainComponent: <ShipmentDetailViewAuthorization />
    }
  },
  SHIPMENT_SEARCH: {
    path: "/search",
    screen: {
      title: "Search Results",
      id: "SHIPMENT_SEARCH",
      showInSidebar: false,
      mainComponent: <ShipmentSearchViewAuthorization />
    }
  },
  LOCATION_MANAGEMENT: {
    path: "/locationmanagement",
    screen: {
      title: "Location Management",
      id: "LOCATION_MANAGEMENT",
      icon: <WebAsset />,
      showInSidebar: hasPrivileges([Privileges.MANAGE_LOCATIONS]),
      mainComponent: <LocationsViewAuthorization />
    }
  },
  LOCATION_DETAIL: {
    path: "/locations/:location_id",
    screen: {
      title: "Location Detail",
      id: "LOCATION_DETAIL",
      showInSidebar: false,
      mainComponent: <LocationDetailAuthorization />
    }
  },
  LOCATION_EDIT: {
    path: "/locations/edit/:location_id",
    screen: {
      title: "Edit Location",
      id: "LOCATION_EDIT",
      showInSidebar: false,
      mainComponent: <LocationEditViewAuthorization />
    }
  },
  USER_MANAGEMENT: {
    path: "/usermanagement",
    screen: {
      title: "User Management",
      id: "USER_MANAGEMENT",
      icon: <WebAsset />,
      showInSidebar: hasPrivileges([Privileges.MANAGE_USERS]),
      mainComponent: <UsersViewAuthorization />
    }
  },
  ORGANIZATION_MANAGEMENT: {
    path: "/organizationmanagement",
    screen: {
      title: "Organization Management",
      id: "ORGANIZATION_MANAGEMENT",
      icon: <WebAsset />,
      showInSidebar: hasPrivileges([Privileges.MANAGE_ORGANIZATIONS]),
      mainComponent: <OrganizationsViewAuthorization />
    }
  },
  REPORTING: {
    path: "/reporting",
    screen: {
      title: "Reporting",
      id: "REPORTING",
      icon: <WebAsset />,
      showInSidebar: false,
      mainComponent: <UnderConstructionView />
    }
  },
  PROFILE: {
    path: "/profile",
    screen: {
      title: "My Profile",
      id: "PROFILE",
      showInSidebar: false,
      mainComponent: <ProfileContainer />
    }
  },
  NOT_FOUND: {
    path: "/notfound",
    screen: {
      title: "Not Found",
      id: "NOT_FOUND",
      icon: <WebAsset />,
      showInSidebar: false,
      mainComponent: <UnderConstructionView />
    }
  },
  CALLBACK: {
    path: "/callback",
    screen: {
      id: "CALLBACK",
      showInSidebar: false,
      mainComponent: <CallbackContainer />
    }
  },
  VERIFY_USER_ERROR: {
    path: "/verifyUserError",
    screen: {
      title: "Verify User Error",
      id: "VERIFY_USER_ERROR",
      showInSidebar: false,
      mainComponent: <ErrorContainer statusCode={StatusCodes.STATUS_CODE_401} />
    }
  },
  ACCESS_FORBIDDEN_ERROR: {
    path: "/accessForbiddenError",
    screen: {
      title: "Access Forbidden Error",
      id: "ACCESS_FORBIDDEN_ERROR",
      showInSidebar: false,
      mainComponent: <ErrorContainer statusCode={StatusCodes.STATUS_CODE_403} />
    }
  }
};

export const routesList = _.values(routesMap);
export const routesByPath = _.keyBy(routesList, "path");
export const routesByLocationType = _.keyBy(routesList, "screen.id");

export function routeForLocation(location, defaultRoute = routesMap.NOT_FOUND) {
  if (location.pathname !== "/callback") {
    authentication.setLastRequestedUrl(location.pathname);
  }
  return _.get(routesByLocationType, location.type, defaultRoute);
}

export function navigationForLocation(location) {
  return location.pathname !== "/registration";
}

export function screenForLocation(location) {
  return routeForLocation(location).screen;
}

export function mainComponentForLocation(location) {
  return screenForLocation(location).mainComponent;
}



// WEBPACK FOOTER //
// ./src/routes.js